"use client";

import React, { useState, useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import classNames from "classnames";
import get from "lodash/get";

// layouts
import SinglePageLayout from "@/layouts/SinglePageLayout";

// components
import BackToSearchResults from "@/components/BackToSearchResults";
import LocationSpaceImages, { ILocationSpaceImage } from "@/components/LocationSpaceImages";
import LocationSinglePageHeader from "./components/LocationSinglePageHeader";
import LocationSpaceInfo from "@/containers/LocationSpaceInfo";
import LocationSpaceDetailsCard from "@/containers/LocationSpaceDetailsCard";
import LocationSpaceStatisticsContainer, { ILocationSpaceStatisticsContainer } from "@/containers/LocationSpaceStatisticsContainer";
import LocationSpacesContainer from "./containers/LocationSpacesContainer";
import LocationFloorPlan from "./containers/LocationFloorPlan";
import LocationFAQ from "./components/LocationFAQ";
const LocationFloorPlanModal = dynamic(() => import("./components/LocationFloorPlanModal"), { ssr: false });
const GoogleMapModal = dynamic(() => import("@/components/Modals/GoogleMapModal"), { ssr: false });

// hooks
import useUserProfile from "@/hooks/useUserProfile";
import { useLocationSinglePageData } from "./hooks";
import { useLockoutModalContext } from "@/contexts/LockoutModalContext";
import { useDimension } from "@/hooks/useDimensions";

// helpers
import customDataLayerPush from "@/helpers/customDataLayerPush";
import getCityQueryListByRegion from "@/helpers/getCityQueryListByRegion";
import { transformLocationImages } from "./helpers";
import locations from "@/api/locations";
import analytics from "@/api/analytics";

// interfaces
import { ISpaceFull } from "@/types/Space";
import { ISingleLocationPage } from "./ISingleLocationPage";
import { ILocationSpaceItem } from "@/types/api/Locations";

// assets
import styles from "@/assets/styles/pages/location-single-page/index.module.scss";


const SingleLocationPageNew = ({ initLocationData, slug, searchParams }: ISingleLocationPage) => {
  const [locationImages, setLocationImages] = useState<ILocationSpaceImage[] | null>(null);
  const [showFloorPlan, setShowFloorPlan] = useState<boolean | null>(null);
  const [isMapModalOpen, setIsMapModalOpen] = useState<boolean>(false);

  const spacesContainerRef = useRef<{
    refBlockForScroll: HTMLDivElement | null; refetch:() => Promise<unknown> } | null>(null);
  const demographicsContainerRef = useRef<HTMLDivElement | null>(null);
  
  const { data: user, isAuthorized } = useUserProfile();
  const { setState } = useLockoutModalContext();

  const [width] = useDimension();

  const isTablet = !!width && width < 920;

  const {
    countryUtils,
    locationData,
    floorOptions,
    spacesList,
    shouldBlurLocation,
    locationTypeToRender,
    collectionsQuery,
  } = useLocationSinglePageData({
    slug,
    isAuthorized,
    isPreview: !slug,
    intiData: initLocationData,
    preview: searchParams.preview
  });

  const isRestrictedView = (!isAuthorized && !searchParams.preview && process.env.NEXT_PUBLIC_APP_WL_CODE === "LOCL" && locationData && locationData.country.name !== "Australia") ||
    (!isAuthorized && !searchParams.preview && locationData && locationData.accessRestricted && process.env.NEXT_PUBLIC_APP_WL_CODE === "WERI")

  const backButtonDefaultQuery = getCityQueryListByRegion({
    countryData: get(countryUtils, "data") || null,
    region: get(locationData, "country.name") === "Australia" ? "australia" : "europe" }
  );

  const activationSpaces = get(locationData, "spacesCount.activationSpaces", 0);
  const mediaOpportunitySpaces = get(locationData, "spacesCount.mediaOpportunitySpaces", 0);

  // methods
  const handleFloorModalOpen = () => {
    setShowFloorPlan(true)
  }
  
  const handleOnAddressClick = () => {
    setIsMapModalOpen(true)
  }

  const handleVirtualTourClick = () => {
    analytics.virtualTourClick({
      location: locationData.id,
      space: null,
    }).catch(() => null);
  };

  const handleSendAnalytics = () => {
    if (!searchParams.preview && slug) {
      locations.getLocationsRecordPageView(slug as string).catch(() => null)
    }
  }

  const handleRestrictedButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isRestrictedView) {
      e.preventDefault();

      setState({
        open: true,
        loginCallback: handleSendAnalytics,
      });
      customDataLayerPush("lo_fo_modal_popup_shown", null, { lofo_type: "soft lofo" });

      return null;
    }
  };

  // effects
  useEffect(() => {
    if (locationData) {
      transformLocationImages(locationData).then((images) => {
        setLocationImages(images);
      });
    }
  }, [JSON.stringify(locationData)]);

  useEffect(() => {
    handleSendAnalytics();
  }, [])

  // renders
  const renderFloorPlanBlock = () => (
    <LocationFloorPlan
      items={floorOptions || []}
      spaces={(spacesList || []) as ILocationSpaceItem[]}
      activationSpacesCount={activationSpaces}
      mediaOpportunitySpacesCount={mediaOpportunitySpaces}
    />
  )

  if (!locationData) {
    return null;
  }

  return (
    <SinglePageLayout
      data-testid="locl-location-single-page"
      className={classNames(
        styles["locl-location-single-page"],
        { [styles["locl-location-single-page_restricted"]]: shouldBlurLocation },
        { [styles["locl-location-single-page_small-padding"]]: (user?.shouldProgressBarBeVisible || !isAuthorized) }
      )}
    >
      {
        !isTablet && (
          <BackToSearchResults defaultQuery={backButtonDefaultQuery && `city__in=${backButtonDefaultQuery}`} />
        )
      }
      <LocationSinglePageHeader
        title={locationData.name}
        locationType={locationTypeToRender}
        collectionsQuery={collectionsQuery}
        isNew={locationData.isNew}
      />
      <LocationSpaceImages
        images={locationImages || []}
        title={locationData.name}
        virtualTourUrl={ process.env.NEXT_PUBLIC_APP_WL_CODE === "LOCL" ? locationData.virtualTourUrl : locationData.whitelabelVirtualTourUrl}
        onFloorPlanClick={handleFloorModalOpen}
        onVirtualTourClick={handleVirtualTourClick}
        isRestricted={isRestrictedView}
        onRestrictedButtonClick={handleRestrictedButtonClick}
        type="location"
        isWithFloorPlan
      />
      <div className={styles["locl-location-single-page__inner"]}>
        <div className={styles["locl-location-single-page__inner__left"]}>
          {
            <LocationSpaceInfo
              description={locationData.description}
              region={locationData.region.name}
              country={locationData.country.name}
              virtualTourUrl={ process.env.NEXT_PUBLIC_APP_WL_CODE === "LOCL" ? locationData.virtualTourUrl : locationData.whitelabelVirtualTourUrl}
              withFloorPlan={true}
              title="Main information"
              descriptionLines={isTablet ? 2 : 6}
              isRestricted={isRestrictedView}
              type="location"
              onAddressClick={handleOnAddressClick}
              onFloorPlanClick={handleFloorModalOpen}
              onVirtualTourClick={handleVirtualTourClick}
              onRestrictedButtonClick={handleRestrictedButtonClick}
            />
          }
          <LocationSpaceDetailsCard
            title="Demographics"
            isCollapsing={width < 920}
            ref={demographicsContainerRef}
          >
            <LocationSpaceDetailsCard.Content>
              <LocationSpaceStatisticsContainer
                demographic={locationData.demographic as unknown as ILocationSpaceStatisticsContainer["demographic"]}
                defaultCurrency={locationData.currency?.code}
                isPreview={!slug}
                isMobileView
                isTabletView
                isCollapsing
              />
            </LocationSpaceDetailsCard.Content>
          </LocationSpaceDetailsCard>
          { !isTablet && renderFloorPlanBlock() }
        </div>
        <div className={styles["locl-location-single-page__inner__right"]}>
          <LocationSpacesContainer
            locationData={locationData}
            locationCurrency={locationData.currency?.code}
            isPreview={!slug}
            ref={spacesContainerRef}
            handleSendAnalytics={handleSendAnalytics}
            isRestricted={isRestrictedView}
            onRestrictedButtonClick={handleRestrictedButtonClick}
          />
          { !!isTablet && renderFloorPlanBlock() }
        </div>
      </div>
      <LocationFAQ faqList={initLocationData?.faq || []} />
      <LocationFloorPlanModal
        floors={floorOptions as unknown as ISpaceFull["floor"][]}
        open={!!showFloorPlan}
        onCancel={() => setShowFloorPlan(false)}
        locationData={locationData}
        isPreview={!slug}
        locationCurrency={locationData.currency?.code}
        onAddToCampaign={() => spacesContainerRef.current && spacesContainerRef.current.refetch()}
        handleSendAnalytics={handleSendAnalytics}
      />
      <GoogleMapModal
        open={isMapModalOpen}
        onCancel={() => setIsMapModalOpen(false)}
        coordinates={{
          lat: locationData.latitude,
          lng: locationData.longitude
        }}
        address={`${locationData.address}, ${locationData.city.name} ${locationData.postCode}`}
      />
    </SinglePageLayout>
  );
}

export default SingleLocationPageNew;
